<template>
  <div class="parallax" ref="parallaxRef">
    <div class="layer layer--0" data-duration="9">
      <div class="layer__track"></div>
    </div>

    <div class="layer layer--1" data-duration="10">
      <div class="layer__track"></div>
    </div>

    <div class="layer layer--2" data-duration="11">
      <div class="layer__track"></div>
    </div>

    <div class="layer layer--3" data-duration="12">
      <div class="layer__track"></div>
    </div>

    <div class="layer layer--4" data-duration="4">
      <div class="layer__track"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted } from 'vue';
import { initCssParallax } from '@/lib/parallax';

const parallaxRef = ref<HTMLDivElement | null>(null);
let parallaxCtrl: ReturnType<typeof initCssParallax> | null = null;

function start(): void {
  if (parallaxCtrl?.resume) {
    try { parallaxCtrl.resume(); } catch {}
    return;
  }

  if (!parallaxCtrl && parallaxRef.value) {
    try {
      parallaxCtrl = initCssParallax(parallaxRef.value);
    } catch {
      parallaxCtrl = null;
    }
  }
}

function pause(): void {
  if (parallaxCtrl?.pause) {
    try { parallaxCtrl.pause(); } catch {}
  } else if (parallaxCtrl?.stop) {
    try { parallaxCtrl.stop(); } catch {}
    parallaxCtrl = null;
  }
}

function stop(): void {
  if (parallaxCtrl?.stop) {
    try { parallaxCtrl.stop(); } catch {}
  }
  parallaxCtrl = null;
}

defineExpose({
  start,
  pause,
  stop,
  resume: start,
});

onUnmounted(() => {
  if (parallaxCtrl?.stop) {
    try { parallaxCtrl.stop(); } catch {}
  }
  parallaxCtrl = null;
});
</script>

<style lang="scss" scoped>
.parallax {
  position: absolute;
  inset: auto 0 25% 0;
  margin: auto;
  width: 100%;
  height: 576px;
  transform: translateY(82px);
}

.layer {
  --tile-width: 576px;

  position: absolute;
  inset: 0;
  z-index: var(--layer-z, 1);
  overflow: hidden;
  isolation: isolate;
  contain: layout paint;

  &__track {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: calc(100% + var(--tile-width));
    display: flex;
    background-image: var(--layer-bg);
    background-repeat: repeat-x;
    background-size: var(--tile-width) 100%;
    background-position: 0 0;
    will-change: transform;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }
}

.layer--0 { --layer-z: 7; --layer-bg: url("@/assets/images/webp/ground_0.webp"); }
.layer--1 { --layer-z: 6; --layer-bg: url("@/assets/images/webp/ground_1.webp"); }
.layer--2 { --layer-z: 5; --layer-bg: url("@/assets/images/webp/ground_2.webp"); }
.layer--3 { --layer-z: 4; --layer-bg: url("@/assets/images/webp/ground_3.webp"); }
.layer--4 { --layer-z: 2; --layer-bg: url("@/assets/images/webp/ground_4.webp"); }
</style>
